import {
  ConfirmationModalTranslations,
  ScorableAssignmentLtiUserMaterial,
  ScorableFreeExerciseLtiUserMaterial,
} from './types';
import { ReactNode, useContext } from 'react';
import HighlightedItem from '@pelckmans/elementis/components/highlighted-item';
import Dialog from '@pelckmans/elementis/components/dialog';
import { useTranslation } from 'react-i18next';
import ModalContext from '../../../../contexts/ModalContext';
import { InitAssignmentSessionResponse, InitFreeExerciseSessionResponse } from '../exercise-manager/api/exercises-api';
import Button from '@pelckmans/elementis/components/button';
import ScorableProgress from '../../../../pages/material/toc/components/ScorableProgress';
import { ScoreBase } from '../../../../api/assignment';
import { calculateScore } from '../../util/calculateScore';

function getPercentage(score: ScoreBase) {
  if (score.status === 'pending-grading') return 100;

  return calculateScore(score);
}

type ConfirmationDialogProps = {
  id: string;
  material: ScorableAssignmentLtiUserMaterial | ScorableFreeExerciseLtiUserMaterial;
  onConfirm: () => void;
  translations: ConfirmationModalTranslations;
  session: InitAssignmentSessionResponse | InitFreeExerciseSessionResponse;
  isOwner: boolean;
  tags: ReactNode;
};

export default function ConfirmationDialog({
  id,
  material,
  translations,
  session,
  onConfirm,
  isOwner,
  tags,
}: ConfirmationDialogProps) {
  const { closeModal } = useContext(ModalContext);

  const { t } = useTranslation();

  function handleClose() {
    closeModal(id);
  }

  if (!session.result) throw new Error('Session result is missing');

  const isRepeatable = isOwner || !material.assignment || material.assignment.repeatable;

  return (
    <Dialog id={id} modal onClose={handleClose} size="md" aria-label={translations.ariaLabel}>
      <Dialog.Header title={translations.title} onClose={handleClose} aria-label={t('ACTIONS.CLOSE')} />
      <Dialog.Body>
        <div className="confirmation-content-container">
          <p className="confirmation-text">
            {isRepeatable ? translations.body.repeatable : translations.body.notRepeatable}
          </p>
          <HighlightedItem
            title={translations.body.exerciseTitle}
            variant="exercise"
            exerciseName={material.name}
            result={{
              title: translations.body.currentResult,
              value: <ScorableProgress score={material.score} />,
            }}
            tags={{
              children: tags,
              'aria-label': undefined,
              role: undefined,
            }}
            percentage={
              material.score.status === 'pending-grading'
                ? undefined
                : {
                    title: translations.body.percent,
                    value: getPercentage(material.score),
                  }
            }
            score={
              material.score.status === 'pending-grading'
                ? undefined
                : {
                    title: translations.body.score,
                    value: `${session.result.score.achieved} / ${session.result.score.maximum}`,
                  }
            }
          />
        </div>
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsLeft={
          isRepeatable
            ? {
                'aria-label': undefined,
                role: undefined,
                children: (
                  <Button prominence="neutral" onClick={onConfirm}>
                    {translations.actions.restart}
                  </Button>
                ),
              }
            : undefined
        }
        buttonGroupPropsRight={{
          'aria-label': undefined,
          role: undefined,
          children: (
            <Button prominence="neutral" onClick={handleClose}>
              {translations.actions.cancel}
            </Button>
          ),
        }}
      />
    </Dialog>
  );
}
